import moment from 'moment';
import dayjs from 'dayjs';

export const INITIAL_INQUIRY_FILTERS = {
  clientSearch: '',
  status: [],
  associate: [],
  tags: [],
  collections: [],
  families: [],
  materials: [],
  stores: [],
  dateRange: null,
};

// Rebuilds inquiry filters saved with useSessionState: dateRange comes back as ISO strings.
export const deserializeInquiryFilters = saved => ({
  ...INITIAL_INQUIRY_FILTERS,
  ...saved,
  dateRange: saved?.dateRange ? saved.dateRange.map(date => (date ? dayjs(date) : null)) : null,
});

/**
 * Map inquiry filters to the query params shared by the /inquiries list and the
 * Rolex export endpoints, so an export contains exactly what the table shows.
 * @param {object} filters - inquiry filter state (see INITIAL_INQUIRY_FILTERS)
 * @param {string} clientSearch - search term to send (the table passes its debounced value)
 */
export const buildInquiryFilterQuery = (filters, clientSearch = filters.clientSearch) => {
  const q = { vendorKey: 'rolex' };
  if (clientSearch) q.clientSearch = clientSearch;
  if (filters.collections?.length) q.collections = filters.collections;
  if (filters.families?.length) q.families = filters.families;
  if (filters.materials?.length) q.materials = filters.materials;
  if (filters.status?.length) q.statuses = filters.status;
  if (filters.associate?.length) q.associateId = filters.associate.map(Number);
  if (filters.tags?.length) q.tag = filters.tags;
  if (filters.stores?.length) q.storeId = filters.stores.map(Number);
  if (filters.dateRange?.[0]) q.startDate = filters.dateRange[0].format('YYYYMMDD');
  if (filters.dateRange?.[1]) q.endDate = filters.dateRange[1].format('YYYYMMDD');
  return q;
};

export const OPEN_STATUSES = ['WAITING', 'ORDERED'];
export const TERMINAL_STATUSES = ['CANCELED', 'FULFILLED'];

export const isTerminalStatus = status => TERMINAL_STATUSES.includes(status);

/**
 * Format an inquiry date (YYYYMMDD string) as a human-readable duration
 * relative to today. Returns "This month" for the current month or
 * "N months" for earlier months.
 */
export const formatInquiryDuration = dateStr => {
  if (!dateStr) return '';
  const months = moment().diff(moment(String(dateStr), 'YYYYMMDD'), 'months');
  return months === 0 ? 'This month' : `${months} months`;
};

/**
 * Transform an inquiry object from the API response to the shape expected by UI components.
 * @param {object} inquiry - Raw inquiry from API
 * @param {object} options - Optional lookup tables
 * @param {Array} options.stores - Store list from auth.stores for storeId → name mapping
 * @param {Array} options.tags - Merchant tags for tagId → name mapping (fallback only)
 */
export const transformInquiry = (inquiry, { stores = [], tags = [] } = {}) => {
  const inquiryDate = inquiry.inquiryDate ? moment(String(inquiry.inquiryDate), 'YYYYMMDD').format('MM/DD/YYYY') : '';

  const durationMonths = formatInquiryDuration(inquiry.inquiryDate);

  const storeName = stores.find(s => s.id === inquiry.storeId)?.name || '';

  // Tags: resolve to [{name, color}] for colored pill rendering
  // API now includes color directly on tag objects: [{tagId, tagName, color}]
  let resolvedTags;
  if (inquiry.tags && inquiry.tags.length > 0 && typeof inquiry.tags[0] === 'object') {
    resolvedTags = inquiry.tags
      .map(t => ({
        name: t.tagName || t.name,
        color: t.color || tags.find(mt => mt.id === t.tagId)?.color || '#818181',
      }))
      .filter(t => t.name);
  } else {
    resolvedTags = (inquiry.tagIds || [])
      .map(id => {
        const lookup = tags.find(t => t.id === id);
        return lookup ? { name: lookup.name, color: lookup.color || '#818181' } : null;
      })
      .filter(Boolean);
  }

  // requestedProducts: [{vendorItemId, mpn, collectionName, image}]
  // Per-product entries for the Rolex tab's Product column (one model per line, name links to the item page).
  const products = (inquiry.requestedProducts || []).map(p => ({
    name: p.collectionName || '',
    mpn: p.mpn || '',
    vendorItemId: p.vendorItemId || null,
  }));
  // First-product fields consumed by the Today / All Rolex inquiry tables (first model + "+N more").
  const firstProduct = inquiry.requestedProducts?.[0];

  // Last inbound date formatting
  const { lastInboundDate } = inquiry;
  const lastInbound = lastInboundDate ? moment(lastInboundDate).format('MM/DD/YYYY') : '';
  let lastInboundDuration = '';
  if (lastInboundDate) {
    const months = moment().diff(moment(lastInboundDate), 'months');
    if (months === 0) lastInboundDuration = 'This month';
    else if (months === 1) lastInboundDuration = '1 month ago';
    else lastInboundDuration = `${months} months ago`;
  }

  return {
    ...inquiry,
    inquiryDate,
    inquiryDateRaw: inquiry.inquiryDate,
    durationMonths,
    associateName: inquiry.associateName || '',
    createdByName: inquiry.createdByName || '',
    storeName,
    tags: resolvedTags,
    products,
    productName: firstProduct?.collectionName || '',
    productMpn: firstProduct?.mpn || '',
    productImage: firstProduct?.image || null,
    productVendorItemId: firstProduct?.vendorItemId || null,
    productCount: inquiry.requestedProducts?.length || 0,
    lastInbound,
    lastInboundDuration,
    totalSpend: inquiry.totalSpend ?? null,
  };
};

/**
 * Format an ISO timestamp or YYYYMMDD date to a friendly display format.
 * Accepts YYYYMMDD as either a string or number (moment would otherwise
 * parse a numeric 20260429 as epoch milliseconds). Other numeric inputs
 * (epoch millisecond timestamps) are passed to moment as-is so they
 * parse correctly.
 */
export const formatInquiryDate = (dateStr, format = 'MMM DD, YYYY') => {
  if (!dateStr && dateStr !== 0) return '';
  const str = String(dateStr);
  const m = /^\d{8}$/.test(str) ? moment(str, 'YYYYMMDD') : moment(dateStr);
  if (!m.isValid()) return str;
  return m.format(format);
};

// --- Inquiry table sorting (server-side) ---
// The /inquiries endpoint sorts the full filtered dataset before paginating, so
// the table must send the sort to the server instead of reordering the current
// page in the browser. These helpers translate antd Table sort events to the
// backend { sortBy, sortOrder } contract and back to the column header arrow.

// Default matches the backend default (inquiryDate, DESC).
export const DEFAULT_INQUIRY_SORT = { sortBy: 'inquiryDate', sortOrder: 'DESC' };

// antd Table column `key` -> backend `sortBy` enum value.
const INQUIRY_SORT_FIELD_BY_COLUMN = {
  clientName: 'clientName',
  inquiryDate: 'inquiryDate',
  lastInbound: 'lastInboundDate',
  totalSpend: 'totalSpend',
};

/**
 * Map an antd Table sort event (column key + 'ascend'|'descend') to the backend
 * { sortBy, sortOrder } contract. Callers handle antd's "cleared" 3rd-click
 * separately (they flip the active column), so `antdOrder` is expected to be set.
 * @param {string} columnKey - the sorted column's `key`
 * @param {'ascend'|'descend'} antdOrder - antd sort direction
 * @returns {{sortBy: string, sortOrder: 'ASC'|'DESC'}}
 */
export const resolveInquirySort = (columnKey, antdOrder) => ({
  sortBy: INQUIRY_SORT_FIELD_BY_COLUMN[columnKey] || DEFAULT_INQUIRY_SORT.sortBy,
  sortOrder: antdOrder === 'ascend' ? 'ASC' : 'DESC',
});

/** Flip a backend sort direction. */
export const flipSortOrder = order => (order === 'ASC' ? 'DESC' : 'ASC');

/**
 * Controlled `sortOrder` for a column header arrow given current sort state.
 * @returns {'ascend'|'descend'|null} non-null only for the actively-sorted column
 */
export const columnSortOrder = (columnKey, sortBy, sortOrder) => {
  if (INQUIRY_SORT_FIELD_BY_COLUMN[columnKey] !== sortBy) return null;
  return sortOrder === 'ASC' ? 'ascend' : 'descend';
};
